import React from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';

interface SettingsFooterBarProps {
  lastSaved: { timestamp: Date; user: string } | null;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
  saveDisabled: boolean;
  saveDisabledReason?: string;
}

export const SettingsFooterBar: React.FC<SettingsFooterBarProps> = ({
  lastSaved,
  hasUnsavedChanges,
  isSaving,
  onCancel,
  onSave,
  saveDisabled,
  saveDisabledReason,
}) => (
  <div className="fixed bottom-0 z-10 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" style={{ left: 'var(--sidebar-width)', right: 0 }}>
    <div className="flex h-16 items-center justify-between gap-4 px-6 max-w-[2000px] mx-auto">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {lastSaved && !hasUnsavedChanges && (
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
            Saved {formatDistanceToNow(lastSaved.timestamp, { addSuffix: true })}
          </span>
        )}
        {hasUnsavedChanges && (
          <span className="flex items-center gap-1.5 text-amber-600">
            <AlertTriangle className="h-3.5 w-3.5" />
            Unsaved changes
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isSaving}
          size="lg"
          className="min-w-[120px]"
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          disabled={isSaving || !hasUnsavedChanges || saveDisabled}
          title={saveDisabledReason}
          size="lg"
          className="min-w-[120px]"
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Save
            </>
          )}
        </Button>
      </div>
    </div>
  </div>
);
